import { Product } from '@/types'
import { ProductTileWrap, ListNumber } from './ProductCarousel.styles'
import ProductTile from './ProductTile'
import { Spacing, Heading } from '@big-red-group/storefront-common-ui'
import Container from './Container'
import { useContext, useState } from 'react'
import { LIST_SIZE } from '@/constant/segment'
import { useProductListImpression } from '@/hooks/AnalyticsImpressionHook'
import { useProductClicked } from '@/hooks/AnalyticsProductHook'
import {
  getPoints,
  getPointsBadgeIcon,
  getPointsBadgeStyles,
  getPointsDescription,
  getPointsStyles,
  getRedeemPoints,
} from '@/helpers/LoyaltyCampaignHelper'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import { ProductListCarousel, SwiperHeadingContainer, SwiperButtons, SwiperLeft, SwiperRight } from './Swiper.styles'
import { useLoyaltyCampaign } from '@/hooks/LoyaltyCampaignHook'
import SpeedbumpCta from '@/components/SpeedbumpCta'
import { generateHtmlSafeId } from '@/helpers/WidgetHelper'
import { DefaultPropertiesContext } from '@/context/DefaultPropertiesContext'
import { CANONICAL_BASE_URL, getUrlPrefix } from '@/helpers/UrlHelper'
import { RichSnippetContext } from '@/context/RichSnippetContext'

export const ProductCarousel: React.FC<{
  title: string
  products: Product[]
  numberedList?: boolean
  handlerKey?: string
  noSpacingTop?: boolean
  displayTitle?: boolean
  viewMoreLink?: string
  viewMoreCta?: string
}> = ({
  title,
  products,
  handlerKey,
  numberedList = false,
  noSpacingTop = false,
  displayTitle = true,
  viewMoreLink,
  viewMoreCta,
}) => {
  const trackProductClicked = useProductClicked()
  const loyaltyCampaign = useLoyaltyCampaign()
  const [lastIndex, setLastIndex] = useState(LIST_SIZE)
  const defaultProperties = useContext(DefaultPropertiesContext)
  const baseUrl = getUrlPrefix(defaultProperties?.agentConfiguration?.handlerKey)
  const componentId = generateHtmlSafeId(title)
  const widgetPrev = `${componentId}-prev`
  const widgetNext = `${componentId}-next`
  // This is used to ensure the first Product List Viewed event will only be fired on first impression
  // TODO: Figure out whether list ID is available
  const firstImpressionRef = useProductListImpression({
    products: products.slice(lastIndex - LIST_SIZE, lastIndex),
    listName: title,
  })

  const handlePrev = () => {
    if (lastIndex === LIST_SIZE) return
    setLastIndex(lastIndex - 1)
  }

  const handleNext = () => {
    if (lastIndex === products.length) return
    setLastIndex(lastIndex + 1)
  }

  const { collect } = RichSnippetContext.useRichSnippetStructuredData()

  collect({
    type: 'Carousel',
    data: products.map((p) => ({
      url: `https://${CANONICAL_BASE_URL}${p.url}`,
      name: p.title,
      image: p.image,
    })),
  })

  return (
    <Container dataMcpId={`${componentId}-product-carousel`} noSpacingTop={noSpacingTop} spacing="S">
      <SwiperHeadingContainer ref={firstImpressionRef}>
        {displayTitle && (
          <Heading data-mcp-id={`${componentId}-product-carousel-heading`} size="XL">
            {title}
          </Heading>
        )}
        {products.length > LIST_SIZE && (
          <SwiperButtons>
            <SwiperLeft data-testid={widgetPrev} className={widgetPrev} onClick={handlePrev}></SwiperLeft>
            <SwiperRight data-testid={widgetNext} className={widgetNext} onClick={handleNext}></SwiperRight>
          </SwiperButtons>
        )}
      </SwiperHeadingContainer>
      {displayTitle && <Spacing />}
      <ProductListCarousel>
        <Swiper
          modules={[Navigation]}
          navigation={{ prevEl: `.${widgetPrev}`, nextEl: `.${widgetNext}` }}
          breakpoints={{
            0: {
              spaceBetween: 16,
              slidesPerView: 1,
            },
            576: {
              spaceBetween: 16,
              slidesPerView: 2,
            },
            1100: {
              spaceBetween: 16,
              slidesPerView: 3,
            },
            1400: {
              spaceBetween: 24,
              slidesPerView: 4,
            },
          }}
        >
          {products.map(
            (
              {
                badges,
                id,
                image,
                price,
                region,
                supplier,
                title: productTitle,
                canonicalRegionUrlSegment,
                url,
                fromPrice,
                levy,
                rating,
                hotDeals,
                geolocations,
                summary,
              },
              index
            ) => (
              <SwiperSlide key={index}>
                <ProductTileWrap>
                  {numberedList && <ListNumber>{index + 1}</ListNumber>}
                  <ProductTile
                    dataMcpId={`${componentId}-product-carousel`}
                    badges={badges}
                    hotDeals={hotDeals}
                    id={id}
                    image={image}
                    price={price}
                    region={region}
                    supplier={supplier}
                    title={productTitle}
                    url={url}
                    canonicalRegionUrlSegment={canonicalRegionUrlSegment}
                    fromPrice={fromPrice}
                    rating={rating}
                    levy={levy}
                    geolocations={geolocations}
                    summary={summary}
                    onClick={() =>
                      // TODO: Figure out how to get list ID (or can we just hash the list title to form a list ID?)
                      trackProductClicked({
                        product: products[index],
                        position: index + 1,
                        isRecommended: true,
                        listTitle: title,
                        isCarousel: true,
                      })
                    }
                    pointsProps={
                      loyaltyCampaign
                        ? {
                            points: getRedeemPoints(fromPrice ?? price)!,
                            description: getPointsDescription(handlerKey),
                            pointsStyles: getPointsStyles(handlerKey),
                          }
                        : undefined
                    }
                    pointsBadgeProps={
                      loyaltyCampaign
                        ? {
                            icon: getPointsBadgeIcon(handlerKey),
                            points: getPoints(fromPrice ?? price, loyaltyCampaign)!,
                            description: getPointsDescription(handlerKey),
                            style: getPointsBadgeStyles(handlerKey),
                          }
                        : undefined
                    }
                  />
                </ProductTileWrap>
              </SwiperSlide>
            )
          )}
          {viewMoreCta && viewMoreLink && (
            <SwiperSlide key={viewMoreCta}>
              <SpeedbumpCta cta={viewMoreCta} link={viewMoreLink} />
            </SwiperSlide>
          )}
        </Swiper>
      </ProductListCarousel>
    </Container>
  )
}

export default ProductCarousel
